import { supabase } from '../../shared/supabase/supabaseClient';
import type { OptimizedProductImages } from './optimizeProductImage';
import {
  catalogImageContentType,
  catalogImageFileExt,
  catalogVariantStoragePath,
} from './catalogImageStorage';
import { decodeBase64, getPublicUrl, uploadToStorageWithRetry } from './storageUploadRetry';

const PRODUCT_IMAGES_BUCKET = 'product-images';

export async function uploadProductImage(params: {
  orgId: string;
  productId: string;
  optimized: OptimizedProductImages;
}): Promise<string> {
  const { orgId, productId, optimized } = params;
  return uploadEntityImage({ orgId, entityId: productId, optimized });
}

export async function uploadProductVariantImage(params: {
  orgId: string;
  variantId: string;
  optimized: OptimizedProductImages;
}): Promise<string> {
  const { orgId, variantId, optimized } = params;
  return uploadEntityImage({ orgId, entityId: variantId, optimized });
}

export async function uploadProductAxisImage(params: {
  orgId: string;
  productId: string;
  axis: string;
  value: string;
  optimized: OptimizedProductImages;
}): Promise<string> {
  const { orgId, productId, axis, value, optimized } = params;
  const entityId = `${productId}-${axis}-${value}`.replace(/[^a-zA-Z0-9-]/g, '-');
  return uploadEntityImage({ orgId, entityId, optimized });
}

async function uploadEntityImage(params: {
  orgId: string;
  entityId: string;
  optimized: OptimizedProductImages;
}): Promise<string> {
  const { orgId, entityId, optimized } = params;

  const ext = catalogImageFileExt(optimized);
  const contentType = catalogImageContentType(ext);
  const variants = [
    { suffix: 'thumb' as const, base64: optimized.thumb.base64 },
    { suffix: 'medium' as const, base64: optimized.medium.base64 },
    { suffix: 'full' as const, base64: optimized.full.base64 },
  ];

  for (const variant of variants) {
    const storagePath = catalogVariantStoragePath(orgId, entityId, variant.suffix, ext);
    try {
      await uploadToStorageWithRetry({
        bucket: PRODUCT_IMAGES_BUCKET,
        path: storagePath,
        body: decodeBase64(variant.base64),
        contentType,
      });
    } catch (err) {
      throw new Error(`No se pudo subir la imagen (${variant.suffix}): ${(err as Error).message}`);
    }
  }

  return getPublicUrl(PRODUCT_IMAGES_BUCKET, catalogVariantStoragePath(orgId, entityId, 'full', ext));
}

export async function removeProductImage(params: {
  orgId: string;
  productId: string;
}): Promise<void> {
  const { orgId, productId } = params;
  return removeEntityImage(orgId, productId);
}

export async function removeProductVariantImage(params: {
  orgId: string;
  variantId: string;
}): Promise<void> {
  const { orgId, variantId } = params;
  return removeEntityImage(orgId, variantId);
}

export async function removeProductAxisImage(params: {
  orgId: string;
  productId: string;
  axis: string;
  value: string;
}): Promise<void> {
  const { orgId, productId, axis, value } = params;
  const entityId = `${productId}-${axis}-${value}`.replace(/[^a-zA-Z0-9-]/g, '-');
  return removeEntityImage(orgId, entityId);
}

async function removeEntityImage(orgId: string, entityId: string): Promise<void> {
  const pathsToRemove = [
    `${orgId}/${entityId}-thumb.webp`,
    `${orgId}/${entityId}-medium.webp`,
    `${orgId}/${entityId}-full.webp`,
    `${orgId}/${entityId}-thumb.jpg`,
    `${orgId}/${entityId}-medium.jpg`,
    `${orgId}/${entityId}-full.jpg`,
    `${orgId}/${entityId}.jpg`,
    `${orgId}/${entityId}.png`,
    `${orgId}/${entityId}.webp`,
  ];

  try {
    await supabase.storage.from('product-images').remove(pathsToRemove);
  } catch (err) {
    console.warn('[productImageRepo] No se pudo borrar las imágenes del bucket', err);
  }
}

// Usa product-images (policies org probadas) con sufijo -video.*; product-media
// queda reservado — su bucket nuevo denega inserts por una anomalía RLS en staging.
const PRODUCT_MEDIA_BUCKET = 'product-images';

/** Reel del producto: mp4/webm en bucket product-media (`<org>/<productId>-video.*`). */
export async function uploadProductVideo(params: {
  orgId: string;
  productId: string;
  base64: string;
  mimeType: string;
}): Promise<string> {
  const { orgId, productId, base64, mimeType } = params;
  // quicktime (mov) → sube como mp4 (ext y contentType consistentes, sino Storage 400).
  const isWebm = mimeType.includes('webm');
  const ext = isWebm ? 'webm' : 'mp4';
  const contentType = isWebm ? 'video/webm' : 'video/mp4';
  const storagePath = `${orgId}/${productId}-video.${ext}`;

  for (const oldExt of ['mp4', 'webm'].filter((e) => e !== ext)) {
    try {
      await supabase.storage.from(PRODUCT_MEDIA_BUCKET).remove([`${orgId}/${productId}-video.${oldExt}`]);
    } catch {
      // ignore — file may not exist
    }
  }

  try {
    await uploadToStorageWithRetry({
      bucket: PRODUCT_MEDIA_BUCKET,
      path: storagePath,
      body: decodeBase64(base64),
      contentType,
    });
  } catch (err) {
    throw new Error(`No se pudo subir el video: ${(err as Error).message}`);
  }

  return `${getPublicUrl(PRODUCT_MEDIA_BUCKET, storagePath)}?t=${Date.now()}`;
}

export async function removeProductVideo(params: {
  orgId: string;
  productId: string;
}): Promise<void> {
  const { orgId, productId } = params;
  for (const ext of ['mp4', 'webm']) {
    try {
      await supabase.storage.from(PRODUCT_MEDIA_BUCKET).remove([`${orgId}/${productId}-video.${ext}`]);
    } catch {
      // ignore
    }
  }
}
